import React from 'react';

import { ErrorType } from '@/__generated__/checkHometaxAccountMutation.graphql';
import { getCorporationRefundData } from '@/components/tax-refund/common/data/corporation-refund-data';
import { getIndisSearchRefundData } from '@/components/tax-refund/common/data/indis-search-refund-data';
import { ellipsisName } from '@/lib/utils/ellipsisName';

type ErrorContent = {
  title: string;
  message: string | React.ReactNode;
  primaryButtonText: string;
  secondaryButtonText?: string;
  errorCode: ErrorType;
};

export const INDIS_JOINT_CERTIFICATE_ERROR_CONTENT: Partial<Record<ErrorType, () => ErrorContent>> = {
  ERR_HOMETAX_NO_REGIST_CERT: () => ({
    title: '홈택스에 인증서를 등록해주세요',
    message:
      '홈택스에 등록되어 있는 인증서로만 환급 조회가 가능해요. 홈택스로 이동해 인증서를 등록한 후 다시 시도해주세요.\n\n홈택스 [인증센터→공동·금융인증서 등록]',
    primaryButtonText: '가이드 보기',
    secondaryButtonText: '확인',
    errorCode: 'ERR_HOMETAX_NO_REGIST_CERT'
  }),

  ERR_HOMETAX_NO_JUMIN_ID: () => ({
    title: '개인 명의 인증서를 선택해주세요',
    message: '사업장 명의가 아닌 개인 명의 인증서로만 조회가 가능해요. 인증서 확인 후 다시 시도해주세요.',
    primaryButtonText: '확인',
    errorCode: 'ERR_HOMETAX_NO_JUMIN_ID'
  }),

  ERR_HOMETAX_EXPIRE_CERT: () => ({
    title: '만료되지 않은 인증서를 선택해주세요',
    message: '만료된 인증서를 선택하셨어요. 발급 기관을 통해 재발급 후 이용하거나, 다른 인증서를 선택해주세요.',
    primaryButtonText: '확인',
    errorCode: 'ERR_HOMETAX_EXPIRE_CERT'
  }),

  ERR_HOMETAX_NO_USER: () => ({
    title: '홈택스 회원가입이 필요해요',
    message: (
      <>
        비즈넵 환급은 홈택스 내역을 토대로 환급금을 계산하고 있어요. 홈택스 가입 후 다시 시도해주세요.
        <br />
        <br />
        <div className="font-bold">*개인 주민등록번호로 가입해주세요.</div>
      </>
    ),
    primaryButtonText: '홈택스로 이동',
    secondaryButtonText: '확인',
    errorCode: 'ERR_HOMETAX_NO_USER'
  }),

  ERR_NO_EQUAL_TIN: () => {
    const indisSearchRefundData = getIndisSearchRefundData();
    return {
      title: `${indisSearchRefundData?.bizName}님의 인증서를 선택해주세요`,
      message: '해당 사용자의 인증서로만 조회할 수 있어요.',
      primaryButtonText: '확인',
      errorCode: 'ERR_NO_EQUAL_TIN' as ErrorType
    };
  },

  ERR_EXIST_FINISH_REFUND: () => ({
    title: '환급 완료한 사용자입니다.',
    message: '다른 계정으로 환급을 완료한 이력이 있어 조회가 불가능합니다.',
    primaryButtonText: '확인',
    errorCode: 'ERR_EXIST_FINISH_REFUND'
  })
};

export const CORPS_JOINT_CERTIFICATE_ERROR_CONTENT: Partial<Record<ErrorType, () => ErrorContent>> = {
  ERR_HOMETAX_NO_REGIST_CERT: () => ({
    title: '홈택스에 인증서를 등록해주세요',
    message:
      '홈택스에 등록되어 있는 인증서로만 환급 조회가 가능해요. 홈택스로 이동해 법인 사업장 인증서를 등록한 후 다시 시도해주세요.\n\n홈택스 [인증센터→공동·금융인증서 등록]',
    primaryButtonText: '가이드 보기',
    secondaryButtonText: '확인',
    errorCode: 'ERR_HOMETAX_NO_REGIST_CERT'
  }),

  ERR_HOMETAX_NO_CORPS_ID: () => ({
    title: '법인사업자 인증서를 선택해주세요',
    message: '개인 또는 개인사업자용 인증서를 선택하셨어요. 인증서 확인 후 다시 시도해주세요.',
    primaryButtonText: '확인',
    errorCode: 'ERR_HOMETAX_NO_CORPS_ID'
  }),

  ERR_HOMETAX_EXPIRE_CERT: () => ({
    title: '만료되지 않은 인증서를 선택해주세요',
    message: '만료된 인증서를 선택하셨어요. 발급 기관을 통해 재발급 후 이용하거나, 다른 인증서를 선택해주세요.',
    primaryButtonText: '확인',
    errorCode: 'ERR_HOMETAX_EXPIRE_CERT'
  }),

  ERR_EXIST_SEARCH_HISTORY: () => ({
    title: '다른 법인사업자 인증서를 선택해주세요',
    message: '이미 내 사업체 목록에 있는 사업장 인증서를 선택하셨어요. 다른 인증서를 선택해주세요.',
    primaryButtonText: '확인',
    errorCode: 'ERR_EXIST_SEARCH_HISTORY'
  }),

  ERR_HOMETAX_NO_USER: () => ({
    title: '홈택스 회원가입이 필요해요',
    message: (
      <>
        비즈넵 환급은 홈택스 내역을 토대로 환급금을 계산하고 있어요. 홈택스 가입 후 다시 시도해주세요.
        <br />
        <br />
        <div className="font-bold">*법인 사업자등록번호로 가입해주세요.</div>
      </>
    ),
    primaryButtonText: '홈택스로 이동',
    secondaryButtonText: '확인',
    errorCode: 'ERR_HOMETAX_NO_USER'
  }),

  ERR_NO_EQUAL_TIN: () => {
    const refundData = getCorporationRefundData();
    const bizName = ellipsisName(refundData?.bizName, undefined, 13);
    return {
      title: `'${bizName}'의 인증서를 선택해주세요`,
      message: '해당 사업체의 인증서로만 조회할 수 있어요.',
      primaryButtonText: '확인',
      errorCode: 'ERR_NO_EQUAL_TIN' as ErrorType
    };
  }
};

export type { ErrorContent };

const getDefaultErrorContent = (errorCode: ErrorType): ErrorContent => ({
  title: '알 수 없는 에러가 발생했습니다',
  message: (errorCode as string) ?? '',
  primaryButtonText: '확인',
  errorCode
});

export const getIndisJointCertificateErrorContent = (errorCode: ErrorType): ErrorContent => {
  const contentGetter = INDIS_JOINT_CERTIFICATE_ERROR_CONTENT[errorCode];
  return contentGetter ? contentGetter() : getDefaultErrorContent(errorCode);
};

export const getCorpsJointCertificateErrorContent = (errorCode: ErrorType): ErrorContent => {
  const contentGetter = CORPS_JOINT_CERTIFICATE_ERROR_CONTENT[errorCode];
  return contentGetter ? contentGetter() : getDefaultErrorContent(errorCode);
};
